import { Component, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SecretsService } from '../../../shared/services/secrets/secrets.service';
import { Secret } from '../../../shared/interfaces/secrets.interface';
import { SecretErrorHandlingUtils } from '../../../shared/utils/secret-error-handling.utils';

interface SecretValueDialogData {
  secret: Secret;
  projectId: number;
}

@Component({
  selector: 'app-secret-value-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './secret-value-dialog.component.html',
  styleUrl: './secret-value-dialog.component.scss',
})
export class SecretValueDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<SecretValueDialogComponent>>(MatDialogRef);
  data = inject<SecretValueDialogData>(MAT_DIALOG_DATA);
  private secretsService = inject(SecretsService);

  loading = signal(false);
  error = signal<string | null>(null);
  secretValue = signal<string | null>(null);
  hideValue = true;
  copySuccess = signal(false);

  ngOnInit(): void {
    this.loadSecretValue();
  }

  private loadSecretValue(): void {
    if (!this.data.secret.gettable) {
      this.error.set('This secret is not marked as gettable');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.secretsService
      .getSecretValue(this.data.projectId, this.data.secret.secretId)
      .subscribe({
        next: (response) => {
          this.secretValue.set(response.value || null);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.error.set(SecretErrorHandlingUtils.getSecretErrorMessage(error));
        },
      });
  }

  getSourceName(): string {
    return 'Source';
  }

  toggleValueVisibility(): void {
    this.hideValue = !this.hideValue;
  }

  async copyToClipboard(): Promise<void> {
    if (!this.secretValue()) return;

    try {
      await navigator.clipboard.writeText(this.secretValue()!);
      this.copySuccess.set(true);
      setTimeout(() => {
        this.copySuccess.set(false);
      }, 2000);
    } catch (_err) {
      // Failed to copy - user can manually copy if needed
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
